import { useCallback, useEffect, useState } from 'react';

const STORAGE_PREFIX = 'ops.drill.focus.';

type StoredFocus = { ctx: string; id: string };

/**
 * One entry per metric. A `scope` (e.g. the outlet drawer's deck) keeps its own
 * entries, so a deck opened on top of another never reads, drops or clears the
 * one underneath.
 */
function storageKey(metricKey: string, scope = ''): string {
  return `${STORAGE_PREFIX}${scope ? `${scope}:` : ''}${metricKey}`;
}

/** The stored tile id, but only for the same scope, outlet and period. */
export function readDrillFocus(
  metricKey: string,
  contextKey: string,
  scope = '',
): string | null {
  try {
    const raw = sessionStorage.getItem(storageKey(metricKey, scope));
    if (!raw) return null;
    const stored = JSON.parse(raw) as Partial<StoredFocus> | null;
    if (stored && stored.ctx === contextKey && typeof stored.id === 'string') {
      return stored.id;
    }
    // Another scope, outlet or period (or an old index-based entry): drop it.
    sessionStorage.removeItem(storageKey(metricKey, scope));
    return null;
  } catch {
    return null;
  }
}

function writeDrillFocus(
  metricKey: string,
  contextKey: string,
  id: string | null,
  scope = '',
) {
  try {
    const key = storageKey(metricKey, scope);
    if (id) sessionStorage.setItem(key, JSON.stringify({ ctx: contextKey, id }));
    else sessionStorage.removeItem(key);
  } catch {
    /* private mode */
  }
}

/**
 * Forget every metric's focused tile, e.g. when the drill closes. With a
 * `scope`, only that deck's entries go (the outlet drawer closing must not
 * wipe the drill underneath it); without one, every entry goes.
 */
export function clearDrillFocus(scope?: string): void {
  const prefix = scope ? `${STORAGE_PREFIX}${scope}:` : STORAGE_PREFIX;
  try {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(prefix)) keys.push(key);
    }
    keys.forEach(key => sessionStorage.removeItem(key));
  } catch {
    /* private mode */
  }
}

/**
 * Focused tile sectionId for the right inspector. Survives metric-tab switches
 * within one scope, outlet and period (`contextKey`); a different context
 * starts with the inspector closed.
 */
export function useDrillFocus(
  metricKey: string,
  contextKey = '',
  scope = '',
): [string | null, (next: string | null) => void] {
  const [focusId, setFocusId] = useState<string | null>(() =>
    readDrillFocus(metricKey, contextKey, scope),
  );

  useEffect(() => {
    setFocusId(readDrillFocus(metricKey, contextKey, scope));
  }, [metricKey, contextKey, scope]);

  const set = useCallback(
    (next: string | null) => {
      setFocusId(next);
      writeDrillFocus(metricKey, contextKey, next, scope);
    },
    [metricKey, contextKey, scope],
  );

  return [focusId, set];
}
